import { Anchor, Avatar, Group, Text, Tooltip } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { pickAvatarInitial } from '@ayocore/platform/client/utils';
import { PartnerView } from '@ayocore/erp/crm/client/components/Partners/views';
import styles from './PartnerCell.module.css';

interface PartnerCellProps {
  partner:
    | {
        id: number;
        name?: string | null;
        full_name?: string | null;
        archived_at?: string | Date | null;
      }
    | null
    | undefined;
  /** Текст-заглушка при отсутствии партнёра */
  fallback?: string;
}

/**
 * Ячейка партнёра — открывает карточку в дравере.
 *
 * @example
 * cell: ({ row }) => <PartnerCell partner={row.original.partner} />
 */
const PartnerCell = ({ partner, fallback = '—' }: PartnerCellProps) => {
  const navigate = useNavigate();
  const drawer = useDrawer(PartnerView, {
    openInTab: props => navigate(`/crm/partners/${props.partnerId}`),
  });

  if (!partner)
    return (
      <Text size='xs' c='dimmed' style={{ fontStyle: 'italic' }}>
        {fallback}
      </Text>
    );

  const label = partner.name || partner.full_name || fallback;
  // Имена часто обёрнуты в кавычки/кавычки-ёлочки/скобки («Ромашка», "Абрамсон").
  // В аватаре показываем первую буквенно-цифровую литеру, пропуская пунктуацию и пробелы.
  const avatarInitial = pickAvatarInitial(label);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    drawer.open({ partnerId: partner.id, isModal: true }, {});
  };

  const isArchived = !!partner.archived_at;

  return (
    <div className={styles.wrapper}>
      <Group gap={8} wrap='nowrap'>
        <Avatar size={24} radius='xl' color='violet'>
          {avatarInitial}
        </Avatar>
        <Anchor component='button' type='button' onClick={handleClick} className={styles.link}>
          <Text size='xs' truncate>
            {label}
          </Text>
        </Anchor>
      </Group>
      {isArchived && (
        <Tooltip label='Архивный' withArrow>
          <IconLock size={13} color='var(--mantine-color-dimmed)' className={styles.lockIcon} />
        </Tooltip>
      )}
    </div>
  );
};

export default PartnerCell;
